import React, { useContext }from 'react'
import propTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import { Formik, Form as FormikForm, useFormikContext } from 'formik'
import toInteger from 'lodash/toInteger'

import { updateSimpleRiskUsingHandler } from './RiskHelper'
import { Card, CardHeading, CardContent, CardFooter } from '../../components/layout/Card'
import { Grid } from '../../components/layout/Grid'
import {SliderFieldWithLabels} from '../../components/Form/Slider'
import { Button } from '../../components/Button'
import { ActionablesContext } from '../../contexts/ActionablesContext'
import { riskMappingValues, riskProfileDescriptions } from './RiskHelper'
import { RiskExplanation } from './RiskExplanation'
import { RiskBreakdown } from './RiskBreakdown'

export const ExternalRiskProfile = (props) => {
  const { riskProfile, onCreateNewScenario} = props
  const { riskReadOnly, location } = useContext(ActionablesContext)
  const riskRating = toInteger(riskProfile.rating) || 3
  const hasNoRiskRating = !riskProfile.rating

  return (
    <Card className='ui-card--risk-profile risk-profile--external'>
      <Formik
        onSubmit={(values, actions) => updateExternalRisk(props, values, actions) }
        initialValues={{ riskRating }}
      >
        <FormikForm>
          <CardHeading>
            <h5 style={{ textAlign: 'left' }}>
              {location === 'onboarding' ? `Select a risk rating to continue` : `Let's analyze your risk`}
            </h5>
          </CardHeading>
          <CardContent>
            { (riskReadOnly || location === 'implementedPlan')
              ? <ReadOnlyRiskBreakdown {...props} />
              : <RiskSlider {...props} />
            }
          </CardContent>
          <Footer onCreateNewScenario={onCreateNewScenario} hasNoRiskRating={hasNoRiskRating}/>
        </FormikForm>
      </Formik>
    </Card>
  )
}

const RiskSlider = (props) => {
  const { riskProfile, inflation } = props
  const riskRating = toInteger(riskProfile.rating) || 3

  return (
    <Grid halfSplit>
      <Main
        riskRating={riskRating}
      />
      <Aside inflation={inflation} />
    </Grid>
  )
}

const ReadOnlyRiskBreakdown = (props) => {
  return (
    <div className='risk-profile'>
      <RiskExplanation {...props} externalRiskProfile />
      <RiskBreakdown {...props} />
    </div>
  )
}

export const Main = ({ riskRating }) =>
    <Box
      display='flex'
      flexDirection='column'
      justifyContent='center'
      alignItems='center'
    >
      <Box
        width='100%'
        m='1rem'
        p='1rem'
      >
          <SliderFieldWithLabels
            title='Risk profile'
            defaultValue={riskRating}
            min={1}
            max={5}
            step={1}
            format={'mappingValues'}
            mappingValues={riskMappingValues}
            name='riskRating'
          />
      </Box>
    </Box>

export const Aside = ({ inflation }) => {
  const { values: { riskRating } } = useFormikContext()
  const riskProfileName = riskMappingValues[toInteger(riskRating)]
  const riskProfileDescription = riskProfileDescriptions[riskProfileName]
  return (
    <Box
      m='2rem'
      textAlign='left'
    >
      <h3>{riskProfileName} - Return goal: {(inflation * 100) + toInteger(riskRating || 3)}%</h3>
      <p>{riskProfileDescription}</p>
    </Box>
  )
}

const Footer = ({ onCreateNewScenario, hasNoRiskRating }) => {
  const { riskReadOnly, location } = useContext(ActionablesContext)
  const { isSubmitting } = useFormikContext()

  if (riskReadOnly && location !== 'implementedPlan')
    return null

  return(
    <CardFooter>
      { location === 'implementedPlan'
        ?
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <p>You can retake your risk questionnaire in scenario planning</p>
            <Button
              primary
              onClick={() => onCreateNewScenario()}
              trackAs='Read only external risk: New scenario'
            >
              Create a new scenario
            </Button>
          </Box>
        :
        <Button disabled={isSubmitting} primary type='submit' trackAs='External risk: Update external risk profile'>
          {isSubmitting ? 'Updating risk profile...' :
            hasNoRiskRating ? 'Create risk profile' : 'Update risk profile'
          }
          </Button>
      }
    </CardFooter>
  )
}

const updateExternalRisk = async (props, values, actions) => {
  const { riskRating } = values
  await updateSimpleRiskUsingHandler(
    props.updateRiskHandler,
    riskRating,
    props.riskProfile,
    props.isWealthManager,
    props.crudActions,
    props.saygScenariosEnabled
  )
  actions.setSubmitting(false)
}

export const ExternalRiskProfileProptypes = {
  /*
   * Function to call to update the risk profile on
   * form submit
   */
  updateRiskHandler: propTypes.func,
  /*
   * Risk profile of the client.
   */
  riskProfile: propTypes.object.isRequired,
  /*
   * isWealthManager flag
   */
  isWealthManager: propTypes.bool.isRequired,
  /*
   * Inflation, required to output the correct return rate
   */
  inflation: propTypes.number.isRequired
}

ExternalRiskProfile.propTypes = ExternalRiskProfileProptypes
